import { createApp } from "vue";
import { createPinia, storeToRefs } from "pinia";
import App from "./app.vue";
import "./styles/style.css";
import { registerComponents } from "./registerComponents";
import { registerIcons } from "./registerIcons";
import router from "@/router";
import { keycloakAuthService } from "@/services/keycloak-auth.service";
import { dateFilter } from "@/helpers/date-time-utility";
import mitt from "mitt";
import { QuillEditor } from "@vueup/vue-quill";
import VueSanitize from "vue-sanitize-directive";
import { Chart, registerables } from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { VueDraggableNext } from "vue-draggable-next";
import { useUserAccountsStore } from "./pinia/useUserAccountsStore";
import { useAuthStore } from "./pinia/useAuthStore";
import { jwtDecode } from "jwt-decode";
import InfiniteLoading from "v3-infinite-loading";
import { setBearerTokenForAllRequests } from "./services/axios";
import { ROUTE_NAME } from "./router/routes.constants";
Chart.register(...registerables);
Chart.register(zoomPlugin);

const emitter = mitt();

const pinia = createPinia();

const loadVueApp = () => {
  const app = createApp(App)
    .use(pinia)
    .use(router)
    .use(VueSanitize)
    .use(registerComponents)
    .use(registerIcons)
    .component("QuillEditor", QuillEditor)
    .component("Draggable", VueDraggableNext)
    .component("InfiniteLoading", InfiniteLoading);
  app.provide("emitter", emitter);
  app.config.globalProperties.$filters = {
    date: dateFilter,
  };
  return app;
};

if (import.meta.env.VITE_APP_SKIP_LOGIN === "true") {
  loadVueApp().mount("#app");
} else {
  const params: any = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams: any, prop) => searchParams.get(prop),
  });
  const brand = params.brand;
  let clientId = "";
  if (brand && brand == "janet") {
    clientId = brand;
  } else {
    clientId = "oventus-connect";
  }
  localStorage.setItem("clientId", clientId);

  keycloakAuthService.init().then(async (keycloakData: any) => {
    const app = loadVueApp();
    const { setSettingsUserProfile } = useUserAccountsStore();
    const { getUserProfile } = storeToRefs(useUserAccountsStore());
    const { setAuthUserProfile, loginApp, resetAuthState } = useAuthStore();
    setAuthUserProfile({
      username: keycloakData.idTokenParsed.preferred_username,
      email: keycloakData.idTokenParsed.email,
      firstName: keycloakData.idTokenParsed.given_name,
      lastName: keycloakData.idTokenParsed.family_name,
      fullName: keycloakData.idTokenParsed.display_name,
      userId: keycloakData.idTokenParsed.LDAP_ID,
      decodedData: jwtDecode(keycloakData.token),
      serviceType: keycloakData.idTokenParsed.service_type ?? "CONNECT3",
    });

    if (keycloakData?.idToken && keycloakData?.token) {
      loginApp({
        accessToken: keycloakData.token,
      });
      setBearerTokenForAllRequests(keycloakData.token);
    } else {
      resetAuthState();
    }

    const isFreshLogin = localStorage.getItem("fresh-login");
    if (isFreshLogin) {
      await setSettingsUserProfile();
    }
    if (getUserProfile.value?.firstServiceToLoad) {
      if (router.hasRoute(getUserProfile.value?.firstServiceToLoad)) {
        router.push({ name: getUserProfile.value?.firstServiceToLoad });
      } else {
        router.push({ name: ROUTE_NAME.COMPOSE });
      }
    }
    app.mount("#app");
  });
}
